import * as React from 'react'
import * as moment from 'moment-timezone'
import * as cronParser from 'cron-parser'
import {Form, FormListener} from '../../../form/Form';
import {FormGroup} from '../../../form/FormGroup';
import {
    SCHEDULED_TOURNAMENT_SERVICE_NAME,
    ScheduledTournamentService
} from "../../../../service/poker/tournament/ScheduledTournamentService";
import {kernel} from "../../../../ioc/IOCHelper";
import {ListScheduledTournamentRequest} from "../../../../../generated/protocol";
import {FormDateTimePicker} from "../../../form/FormDateTimePicker";

interface State {
    isFetching: boolean,
    fromDate: number,
    toDate: number,
    validationMap?: any,
}

export class ScheduledTournamentsDataExport extends React.Component<{}, State> implements FormListener<ListScheduledTournamentRequest>{
    scheduledTournamentService: ScheduledTournamentService;
    scheduledTournamentListRequest: ListScheduledTournamentRequest

    constructor() {
        super();
        this.state = {
            isFetching: false,
            fromDate: moment().utc().valueOf(),
            toDate: moment().utc().add(3, 'months').valueOf(),
            validationMap: {},
        };
        this.scheduledTournamentService = kernel.get<ScheduledTournamentService>(SCHEDULED_TOURNAMENT_SERVICE_NAME);
        this.scheduledTournamentListRequest = {
            orderBy: "id",
            archived: false,
            currencyCode: null,
            offset: 0,
            limit: 5000
        }
        this.onSubmit = this.onSubmit.bind(this)
    }

    formDidUpdate(form: ListScheduledTournamentRequest, validationMap: any): void {
        this.setState({
            ...this.state,
            ...form,
            validationMap,
        });
    }

    onSubmitError(): void {
        console.error("Error in exporting scheduled tournament data!");
    }

    // Quartz Schedule used a different day of week format: 1-7 or SUN-SAT
    // Compared to the Unix day of week format: (0 - 7, 1L - 7L) (0 or 7 is Sun)
    convertFromQuartzSchedulerCronExpression(cronExpression: string): string {
        const dayOfWeekFieldNo = 5
        const cronExpressionItems = cronExpression.split(' ')
        cronExpressionItems[dayOfWeekFieldNo] =
            cronExpressionItems[dayOfWeekFieldNo]
                .replace('1', '0')
                .replace('2', '1')
                .replace('3', '2')
                .replace('4', '3')
                .replace('5', '4')
                .replace('6', '5')
                .replace('7', '6')
        return cronExpressionItems.join(' ')
    }

    easternTimeConversion(dateValue: number){
        return moment.utc(dateValue).tz('America/New_York')
    }

    timeZoneOffsetFromUTC(dateValue: number, timeZone: any){
        return moment.utc(dateValue).valueOf() - (timeZone.utcOffset(dateValue) * 60 * 1000);
    }

    timeZoneOffsetToUTC(dateValue: number, timeZone: any){
        return moment.utc(dateValue).valueOf() + (timeZone.utcOffset(dateValue) * 60 * 1000);
    }

    generateEndTimeOutputs(schedule) {
        const scheduleEndDateUTC = moment.utc(schedule.endDate).format('YYYY-MM-DD');
        const scheduleEndTimeUTC = moment.utc(schedule.endDate).format('HH:mm:ss');

        const easternTimeEndDate = this.easternTimeConversion(schedule.endDate);
        const scheduleEndDateET = easternTimeEndDate.format('YYYY-MM-DD');
        const scheduleEndTimeET = easternTimeEndDate.format('HH:mm:ss');

        return {
            endDateUTC: scheduleEndDateUTC,
            endTimeUTC: scheduleEndTimeUTC,
            endDateET: scheduleEndDateET,
            endTimeET: scheduleEndTimeET
        }

    }

    calculateFutureStartTimes(schedule: any) {
        const timeZone = moment.tz.zone(schedule.timeZoneId);
        const scheduleStartTime = moment.utc(schedule.startDate).valueOf();
        const scheduleEndTime = moment.utc(schedule.endDate).valueOf();
        const filterStartTime = Math.max(scheduleStartTime, this.state.fromDate);
        const filterEndTime = Math.min(scheduleEndTime, this.state.toDate);

        if (filterEndTime < filterStartTime){
            return []
        }

        const startTimes = [];

        const convertedFilterStartTime = this.timeZoneOffsetFromUTC(filterStartTime, timeZone);
        const convertedFilterEndTime = this.timeZoneOffsetFromUTC(filterEndTime, timeZone);

        const options = {
            currentDate: new Date(convertedFilterStartTime),
            endDate: new Date(convertedFilterEndTime),
            utc: true,
        };

        try {
            const cronSchedule = this.convertFromQuartzSchedulerCronExpression(schedule.cronSchedule)
            const interval = cronParser.parseExpression(cronSchedule, options);
            while (true) {
                try {
                    const next = interval.next().toDate();
                    const utcNextTime = moment.utc(this.timeZoneOffsetToUTC(next.getTime(), timeZone));
                    const etNextTime = this.easternTimeConversion(utcNextTime.valueOf());
                    const startDateAndTime = {
                        startDateUTC: utcNextTime.format('YYYY-MM-DD'),
                        startTimeUTC: utcNextTime.format('HH:mm:ss'),
                        startDateET: etNextTime.format('YYYY-MM-DD'),
                        startTimeET: etNextTime.format('HH:mm:ss'),
                    };
                    startTimes.push(startDateAndTime);
                } catch (err) {
                    break; // End of cron expression iterations
                }
            }
        } catch (err) {
            console.log("Error parsing cron schedule: ", err.message);
        }
        return startTimes;
    }

    onSubmit() {
        if (this.state.fromDate >= this.state.toDate) {
            console.error("To Date must be greater than From Date.");
            return;
        }

        this.setState({
            ...this.state,
            isFetching: true,
        })
        let allConfigs = [];

        const fetchConfigData = () => {
            return this.scheduledTournamentService.list(this.scheduledTournamentListRequest)
                .then(response => {
                    allConfigs = allConfigs.concat(response.items)

                    if (response.items.length < this.scheduledTournamentListRequest.limit) {
                        return allConfigs;
                    } else {
                        this.scheduledTournamentListRequest.offset += this.scheduledTournamentListRequest.limit;
                        return fetchConfigData();
                    }
                })
                .catch(error => {
                    console.error("Error fetching config data: ", error);
                    return [];
                });
        };

        fetchConfigData()
            .then((data: any[]) => {
                const csvData = [];

                data.forEach(item => {
                    const rebuyConfiguration = item.configuration.rebuyConfiguration;
                    const mysteryBountyPrizeStructure = item.configuration.mysteryBountyPrizeStructure;
                    const futureStartTimes = this.calculateFutureStartTimes(item.schedule);

                    const endTimeOutputs = this.generateEndTimeOutputs(item.schedule);
            
                    // Note: This checkbox exists in the template to control the form. This logic is trying to determine
                    // if the checkbox was checked, based on the default unchecked state values.
                    const isRebuysAndAddonsCheckBoxEnabled = !(
                        rebuyConfiguration.numberOfRebuysAllowed === 0 &&
                        rebuyConfiguration.addOnsEnabled === false &&
                        rebuyConfiguration.numberOfLevelsWithRebuys === 0 &&
                        rebuyConfiguration.rebuyCost === null &&
                        rebuyConfiguration.chipsForRebuy === null &&
                        rebuyConfiguration.addOnCost === null &&
                        rebuyConfiguration.chipsForAddOn === null &&
                        rebuyConfiguration.maxStackForRebuy === null
                    )

                    futureStartTimes.forEach(time => {
                        const boostAwardTickets = item.prizeBoostConfiguration && item.prizeBoostConfiguration.tickets
                            ? item.prizeBoostConfiguration.tickets.map(ticket => ticket.name).join('\n')
                            : "";

                        const satelliteAwardTickets = item.satelliteConfiguration && item.satelliteConfiguration.satellite.tickets
                            ? item.satelliteConfiguration.satellite.tickets.map(ticket => ticket.name).join('\n')
                            : "";

                        csvData.push({
                            templateId: item.tournamentTemplateId,
                            startDateUTC: time.startDateUTC,
                            startTimeUTC: time.startTimeUTC,
                            endDateUTC: endTimeOutputs.endDateUTC,
                            endTimeUTC: endTimeOutputs.endTimeUTC,
                            startDateET: time.startDateET,
                            startTimeET: time.startTimeET,
                            endDateET: endTimeOutputs.endDateET,
                            endTimeET: endTimeOutputs.endTimeET,
                            name: item.configuration.name,
                            currency: item.configuration.currency,
                            buyIn: item.configuration.buyIn,
                            bountyFee: item.configuration.bountyFee,
                            mysteryBountyFee: item.configuration.mysteryBountyFee,
                            fee: item.configuration.fee,
                            isBountyTournament: item.configuration.hasBounty,
                            progressivePercentage: item.configuration.progressivePercentage,
                            isMysteryBounty: item.configuration.hasMysteryBounty,
                            guaranteedPrizePool: item.configuration.guaranteedPrizePool,
                            mysteryBountyGuaranteedPrizePool: item.configuration.mysteryBountyGuaranteedPrizePool,
                            mysteryBountyPrizeStructureName: mysteryBountyPrizeStructure ? mysteryBountyPrizeStructure.name : "",
                            payoutStructureName: item.configuration.payoutStructure.name,
                            seatsPerTable: item.configuration.seatsPerTable,
                            startingChips: item.configuration.startingChips,
                            blindsStructureName: item.configuration.blindsStructure.name,
                            lateRegistrationLevel: item.configuration.lateRegistrationLevel,
                            timingProfileName: item.configuration.timingType.name,
                            enableRebuysAndAddons: isRebuysAndAddonsCheckBoxEnabled,
                            numberOfRebuysAllowed: rebuyConfiguration && rebuyConfiguration.numberOfRebuysAllowed ? rebuyConfiguration.numberOfRebuysAllowed : "",
                            rebuyBlindsLevels: rebuyConfiguration && rebuyConfiguration.numberOfLevelsWithRebuys ? rebuyConfiguration.numberOfLevelsWithRebuys : "",
                            rebuyCost: rebuyConfiguration && rebuyConfiguration.rebuyCost ? rebuyConfiguration.rebuyCost : "",
                            chipsForRebuy: rebuyConfiguration && rebuyConfiguration.chipsForRebuy ? rebuyConfiguration.chipsForRebuy : "",
                            addOnsEnabled: rebuyConfiguration ? rebuyConfiguration.addOnsEnabled : "",
                            addOnCost: rebuyConfiguration && rebuyConfiguration.addOnCost ? rebuyConfiguration.addOnCost : "",
                            chipsForAddOn: rebuyConfiguration && rebuyConfiguration.chipsForAddOn ? rebuyConfiguration.chipsForAddOn : "",
                            rebuyAndAddonTimeout: rebuyConfiguration && rebuyConfiguration.rebuyAndAddonTimeout ? rebuyConfiguration.rebuyAndAddonTimeout : "",
                            variant: item.configuration.variant,
                            betStrategy: item.configuration.betStrategy,
                            synchronizeBreaks: item.configuration.synchronizeBreaks,
                            minPlayers: item.configuration.minPlayers,
                            maxPlayers: item.configuration.maxPlayers,
                            isPrizeBoostEnabled: item.prizeBoostConfiguration ? item.prizeBoostConfiguration.enabled : "",
                            boostAmount: item.prizeBoostConfiguration ? item.prizeBoostConfiguration.boostCash : "",
                            boostAwardTickets: boostAwardTickets,
                            passwordEnabled: item.configuration.passwordEnabled,
                            password: item.configuration.password,
                            isShootout: item.configuration.isShootout,
                            isSatellite: item.satelliteConfiguration ? item.satelliteConfiguration.isSatellite : "",
                            satelliteToDummy: item.configuration.satelliteToDummy,
                            mustPlayTargetRightAway: item.satelliteConfiguration ? item.satelliteConfiguration.satellite.mustPlay : "",
                            preventRegistrationIfRegisteredToTarget: item.satelliteConfiguration ? item.satelliteConfiguration.satellite.preventRegistrationIfRegisteredToTarget : "",
                            returnAmount: item.satelliteConfiguration && item.satelliteConfiguration.satellite.returnAmount ? item.satelliteConfiguration.satellite.returnAmount : "",
                            satelliteAwardTickets: satelliteAwardTickets,
                            isTarget: item.satelliteConfiguration ? item.satelliteConfiguration.isTarget : "",
                            dummyTournament: item.configuration.dummyTournament,
                            allowsDirectBuyIn: item.satelliteConfiguration ? item.satelliteConfiguration.target.allowsDirectBuyIn : "",
                            tags: item.configuration.tags || "",
                            description: item.configuration.description
                        });
                    });
                })
                const csvContent = this.convertToCSV(csvData);
                this.downloadCSV(csvContent, 'scheduled-tournaments-data.csv');
        }).catch((err: any) => {
            console.error("Error converting and downloading to CSV: ", err)
        }).finally(() => {
            this.setState({
                ...this.state,
                isFetching: false,
            })
        });
    }

    convertToCSV(data: any) {
        const headers = [
            "Template ID", "Start Date-UTC", "Start Time-UTC", "End Date-UTC", "End Time-UTC", "Start Date-ET", "Start Time-ET", "End Date-ET", "End Time-ET", 
            "Name", "Currency", "Buy In", "Bounty Fee", "Mystery Bounty Fee",
            "Fee", "Is Bounty Tournament", "Progressive Percentage", "Is Mystery Bounty", "Guaranteed Prize Pool",
            "Mystery Bounty Guaranteed Prize Pool", "Mystery Bounty Prize Structure Name", "Payout Structure Name",
            "Seats Per Table", "Starting Chips", "Blinds Structure Name", "Late Registration Level", "Timing Profile Name",
            "Enable Rebuys and Add-ons", "Number Of Rebuys Allowed", "Rebuy Blinds Levels", "Rebuy Cost", "Chips For Rebuy",
            "Add Ons Enabled", "Add On Cost", "Chips for Add On", "Rebuy And Addon Timeout", "Variant", "Bet Strategy",
            "Synchronize Breaks", "Min Players", "Max Players", "Is Prize Boost Enabled", "Boost Amount", "Boost Award Tickets",
            "Password Enabled", "Password", "Is Shootout", "Is Satellite", "Satellite To Dummy", "Must Play Target Right Away",
            "Prevent Registration If Registered To Target", "Return Amount", "Satellite Award Tickets", "Is Target",
            "Dummy Tournament", "Allows Direct Buy In", "Tags", "Description"
        ];

        const csvRows = [headers.join(',')];

        function escapeCSV(value: any) {
            if (value == null) {
                return "";
            }
            value = String(value);
            if (value.includes('"')) {
                value = value.replace(/"/g, '""');
            }
            if (value.includes(',') || value.includes('\n')) {
                value = `"${value}"`;
            }
            return value;
        }

        data.forEach(item => {
            const values = [
                item.templateId, item.startDateUTC, item.startTimeUTC, item.endDateUTC, item.endTimeUTC, item.startDateET, item.startTimeET, item.endDateET, item.endTimeET, 
                escapeCSV(item.name), item.currency, item.buyIn,
                item.bountyFee, item.mysteryBountyFee, item.fee, item.isBountyTournament, item.progressivePercentage,
                item.isMysteryBounty, item.guaranteedPrizePool, item.mysteryBountyGuaranteedPrizePool,
                item.mysteryBountyPrizeStructureName, item.payoutStructureName, item.seatsPerTable, item.startingChips,
                item.blindsStructureName, item.lateRegistrationLevel, item.timingProfileName, item.enableRebuysAndAddons,
                item.numberOfRebuysAllowed, item.rebuyBlindsLevels, item.rebuyCost, item.chipsForRebuy, item.addOnsEnabled,
                item.addOnCost, item.chipsForAddOn, item.rebuyAndAddonTimeout, item.variant, item.betStrategy,
                item.synchronizeBreaks, item.minPlayers, item.maxPlayers, item.isPrizeBoostEnabled, item.boostAmount,
                item.boostAwardTickets, item.passwordEnabled, item.password, item.isShootout, item.isSatellite,
                item.satelliteToDummy, item.mustPlayTargetRightAway, item.preventRegistrationIfRegisteredToTarget,
                item.returnAmount, item.satelliteAwardTickets, item.isTarget, item.dummyTournament, item.allowsDirectBuyIn,
                escapeCSV(item.tags), escapeCSV(item.description)
            ];

            csvRows.push(values.join(','));
        });

        return csvRows.join('\n');
    }

    downloadCSV(content, filename) {
        const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }

    render() {
        return (
            <div className="ibox float-e-margins">
                <div className="ibox-title">
                    <h5>Export Scheduled Tournament Data</h5>
                </div>
                <div className="ibox-content">
                    <Form className="form-horizontal" formListener={this} modelObject={this.state} validationMap={this.state.validationMap}>
                        <FormGroup className="form-group" model="fromDate">
                            <label className="col-lg-2 control-label">From Date (UTC):</label>
                            <div className="col-lg-10">
                                <FormDateTimePicker
                                    timeZoneId="UTC"
                                    model="fromDate"
                                    minDate={moment.utc().toDate()}
                                />
                            </div>
                        </FormGroup>
                        <FormGroup className="form-group" model="toDate">
                            <label className="col-lg-2 control-label">To Date (UTC):</label>
                            <div className="col-lg-10">
                                <FormDateTimePicker
                                    timeZoneId="UTC"
                                    model="toDate"
                                    minDate={moment.utc().toDate()}
                                />
                            </div>
                        </FormGroup>
                        <div className="form-group">
                            <div className="col-lg-offset-2 col-lg-10">
                                <button
                                    className="btn btn-primary"
                                    onClick={this.onSubmit}
                                    disabled={this.state.isFetching}
                                >
                                    <i className="fa fa-download"></i> Export to CSV
                                </button>
                            </div>
                        </div>
                    </Form>
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/app/ui/view/poker/tournament/ScheduledTournamentsDataExport.tsx